import { Card, Stack, Table, TableBody, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { Rental } from "src/models";
import { rentalTrailerType } from "src/constants";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import { TruxTableCell } from "src/components/trux-table-cell";
import { RentalStatusStageChip } from "./rental-status";

type RentalsTableProps = {
  title?: string;
  subtitle?: string;
  rentals: Rental[];
  onRentalSelected: (rental: Rental) => void;
  // overrides the stage chip, e.g. when an outbound or return waits for review
  statusChip?: (rental: Rental) => React.ReactNode;
};

export function RentalsTable({ title, subtitle, rentals, onRentalSelected, statusChip }: RentalsTableProps) {
  const { sortedList, sortData } = useSort(rentals, "updatedAt", "desc");
  const lines = sortedList || [];

  return (
    <Card>
      {title && (
        <Stack px={2} pt={2}>
          <Typography variant="h6">{title}</Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Stack>
      )}
      <TableContainer sx={{ position: "relative", overflow: "scroll" }}>
        <Table>
          <TableHead>
            <TableRow>
              <SortableHeader sortKey="trailerUnitNumber" sortData={sortData}>
                Unit Number
              </SortableHeader>
              <SortableHeader sortKey="trailerType" sortData={sortData}>
                Type
              </SortableHeader>
              <SortableHeader sortKey="customerCompanyName" sortData={sortData}>
                Customer
              </SortableHeader>
              <SortableHeader sortKey="rentalStatus" sortData={sortData}>
                Stage
              </SortableHeader>
              <SortableHeader sortKey="updatedAt" sortData={sortData}>
                Updated
              </SortableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((rental) => (
              <TableRow
                hover
                key={rental.id}
                sx={{ cursor: "pointer" }}
                onClick={() => onRentalSelected(rental)}
              >
                <TruxTableCell>{rental.trailerUnitNumber}</TruxTableCell>
                <TruxTableCell>{rentalTrailerType(rental.trailerType)?.label || "-"}</TruxTableCell>
                <TruxTableCell>
                  {`${rental.customerFirstName} ${rental.customerLastName} (${rental.customerCompanyName})`.trim()}
                </TruxTableCell>
                <TruxTableCell>
                  {statusChip ? statusChip(rental) : <RentalStatusStageChip rental={rental} />}
                </TruxTableCell>
                <TruxTableCell>
                  {rental.updatedAt
                    ? DateTime.fromISO(rental.updatedAt).toFormat("MM/dd/yyyy HH:mm")
                    : ""}
                </TruxTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}
